import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API });

// ---- Portfolios ----
export const apiListPortfolios = () => http.get("/portfolios").then((r) => r.data);
export const apiGetPortfolio = (id) => http.get(`/portfolios/${id}`).then((r) => r.data);
export const apiCreatePortfolio = (data) => http.post("/portfolios", data).then((r) => r.data);
export const apiUpdatePortfolio = (id, data) => http.put(`/portfolios/${id}`, data).then((r) => r.data);
export const apiDeletePortfolio = (id) => http.delete(`/portfolios/${id}`).then((r) => r.data);
export const apiAddReview = (pid, blockId, data) =>
  http.post(`/portfolios/${pid}/reviews/${blockId}`, data).then((r) => r.data);

// ---- Quotes ----
export const apiListQuotes = () => http.get("/quotes").then((r) => r.data);
export const apiCreateQuote = (data) => http.post("/quotes", data).then((r) => r.data);
export const apiUpdateQuote = (id, data) => http.put(`/quotes/${id}`, data).then((r) => r.data);
export const apiDeleteQuote = (id) => http.delete(`/quotes/${id}`).then((r) => r.data);

// ---- Pages ----
export const apiGetPage = (id) => http.get(`/pages/${id}`).then((r) => r.data);
export const apiUpdatePage = (id, data) => http.put(`/pages/${id}`, data).then((r) => r.data);

// ---- Clients (CRM) ----
export const apiListClients = () => http.get("/clients").then((r) => r.data);
export const apiCreateClient = (data) => http.post("/clients", data).then((r) => r.data);
export const apiUpdateClient = (id, data) => http.put(`/clients/${id}`, data).then((r) => r.data);
export const apiDeleteClient = (id) => http.delete(`/clients/${id}`).then((r) => r.data);
export const apiAddPayment = (cid, data) =>
  http.post(`/clients/${cid}/payments`, data).then((r) => r.data);
export const apiDeletePayment = (cid, pid) =>
  http.delete(`/clients/${cid}/payments/${pid}`).then((r) => r.data);
export const apiListContracts = () => http.get("/contracts").then((r) => r.data);
export const apiSaveContract = (c) => http.put(`/contracts/${c.id}`, c).then((r) => r.data);
export const apiDeleteContract = (id) => http.delete(`/contracts/${id}`).then((r) => r.data);
export const apiAddContractRenewal = (id, body) => http.post(`/contracts/${id}/renewals`, body).then((r) => r.data);
export const apiDeleteContractRenewal = (id, rid) => http.delete(`/contracts/${id}/renewals/${rid}`).then((r) => r.data);

// ---- Notifications ----
export const apiListNotifications = () => http.get("/notifications").then((r) => r.data);
export const apiRefreshNotifications = () => http.post("/notifications/refresh").then((r) => r.data);
export const apiMarkAllRead = () => http.post("/notifications/read-all").then((r) => r.data);
export const apiDeleteNotification = (id) => http.delete(`/notifications/${id}`).then((r) => r.data);

// ---- Push ----
export const apiPushPublicKey = () => http.get("/push/public-key").then((r) => r.data);
export const apiPushSubscribe = (subscription) =>
  http.post("/push/subscribe", { subscription }).then((r) => r.data);
export const apiPushTest = () => http.post("/push/test").then((r) => r.data);

// ---- Files ----
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB por trozo

export const apiUploadFile = async (file, onProgress, { purpose = "", onStage } = {}) => {
  const report = (p) => {
    if (onProgress) onProgress(Math.max(0, Math.min(100, Math.round(p))));
  };
  // Archivos pequeños: una sola subida.
  if (file.size <= CHUNK_SIZE && !purpose) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await http.post("/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total) report((e.loaded / e.total) * 100);
      },
    });
    report(100);
    return { id: data.id, url: `/api/files/${data.id}` };
  }

  // Archivos grandes (videos): subida por partes, más confiable en móvil.
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const total = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const form = new FormData();
    form.append("upload_id", uploadId);
    form.append("index", String(i));
    form.append("total", String(total));
    form.append("file", blob, "chunk");
    await http.post("/upload/chunk", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (e.total) {
          const frac = e.loaded / e.total;
          report(((i + frac) / total) * 100 * 0.98);
        }
      },
    });
    report(((i + 1) / total) * 100 * 0.98);
  }
  report(99);
  const cform = new FormData();
  cform.append("upload_id", uploadId);
  cform.append("filename", file.name || "file");
  cform.append("content_type", file.type || "application/octet-stream");
  if (purpose) cform.append("purpose", purpose);
  const { data } = await http.post("/upload/complete", cform, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  if (data.job) {
    // Conversión en el servidor (p. ej. video → música): esperamos a que termine.
    if (onStage) onStage("converting");
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      let job;
      try {
        job = (await http.get(`/upload/job/${data.job}`)).data;
      } catch (e) {
        continue;
      }
      if (job.status === "done") {
        report(100);
        return { id: job.path.split("/").pop(), url: job.path };
      }
      if (job.status === "error") throw new Error(job.error || "conversion failed");
    }
  }
  report(100);
  return { id: data.id, url: `/api/files/${data.id}` };
};
